import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "../../ui/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
// import { format } from "date-fns";
// import { CalendarIcon } from "lucide-react";
import { Input } from "../../ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
// import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
// import { Calendar } from "../../ui/calendar";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../ui/dialog";
import { FiCompass } from "react-icons/fi";
import {
  useGetAllTargets,
  useGetConfigs,
  useGetPorts,
  useGetScanners,
} from "../../../helpers/queries/targets/useGetTargets";
import { useGetAllCredentials } from "../../../helpers/queries/credentials/useGetCredentials";
import { createScan } from "../../../helpers/mutations/targets";
import { convertToUTC } from "../../../lib/utils";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { useGetAssetGroups } from "../../../helpers/queries/scans/useGetInventory";
import { Label } from "../../ui/label";
// import { cn } from "../../../lib/utils";

const ScanAssetModal = () => {
  // const [portType, setPortType] = useState("");
  const [open, setOpen] = useState(false);
  // const [openDate, setOpenDate] = useState(false);
  const [scheduleScan, setScheduleScan] = useState(false);
  const [isAssetGroup, setIsAssetGroup] = useState(true);
  const [autoStartScan, setAutoStartScan] = useState(false);
  const { data: targets } = useGetAllTargets();
  const { data: assetGroupList } = useGetAssetGroups(0);
  const { data: ports } = useGetPorts();
  const { data: scanners } = useGetScanners();
  const { data: configs } = useGetConfigs();
  let { data: credentials } = useGetAllCredentials();

  credentials = credentials?.data ?? [];

  const targetOptions = targets?.data?.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const portOptions = ports?.map((item) => ({
    value: item["@id"],
    label: item.name,
  }));

  const selectedPort = portOptions?.find(
    (type) => type.label === "All IANA assigned TCP and UDP"
  );

  const scannerOptions = scanners?.map((item) => ({
    value: item["@id"],
    label: item.name,
  }));

  const configOptions = configs?.map((item) => ({
    value: item["@id"],
    label: item.name,
  }));

  const selectedConfig = configOptions?.find(
    (type) => type.label === "Full and fast"
  );

  const base = {
    target: z
      .string({ required_error: "Target is required" })
      .min(1, "Please select a target"), // String validation
    // ports: z
    //   .string({ required_error: "Port is required" })
    //   .min(1, "Please select a port"), // String validation
    scanners: z
      .string({ required_error: "Scanner is required" })
      .min(1, "Please select a scanner"), // String validation
    // configs: z
    //   .string({ required_error: "Config is required" })
    //   .min(1, "Please select a config"), // String validation
    credential: z.string().optional(), // (optional)
    scanType: z.string().optional(), // (optional)
  };

  const scheduleField = {
    scheduleType: z.string().min(1, "Please select a type"),
    startDate: z.string().min(1, "Please select a date"),
    startTime: z.string().min(1, "Please select a time"),
  };

  const formSchema = scheduleScan
    ? z.object({ ...base, ...scheduleField })
    : z.object(base);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      target: "",
      // ports: "",
      scanners: "",
      credential: "",
      // configs: "",
      scheduleType: "",
      startDate: "",
      startTime: "",
    },
  });

  const { watch, reset } = form;

  const targetValue = watch("target");

  let targetType = "";
  if (isAssetGroup) {
    targetType =
      assetGroupList?.data?.find((group) => String(group.id) === targetValue)
        ?.asset_type || "";
  } else {
    targetType =
      targets?.data?.find((target) => String(target.id) === targetValue)
        ?.asset_type || "";
  }

  // Watch target and asset group toggle
  useEffect(() => {
    // reset the target field value when asset type changes
    form.setValue("target", "");
  }, [isAssetGroup, form]);

  const filteredScannerOptions =
    targetType === "web"
      ? scannerOptions?.filter((opt) => opt.label === "TuringTower Web Scanner")
      : targetType === "infrastructure"
      ? scannerOptions?.filter(
          (opt) =>
            opt.label === "TuringTower Default" ||
            opt.label === "TuringTower Agent"
        )
      : [];

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { mutate, isPending } = useMutation({
    mutationFn: createScan,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scans"] });
      queryClient.invalidateQueries({ queryKey: ["targets"] });
      toast("Successful");
      setOpen(false);
      reset();
      if (location.pathname !== "/dashboard/scan-history") {
        navigate("/dashboard/scan-history");
      }
    },
    onError: (data) => {
      toast(data.message);
    },
  });

  const onSubmit = (data) => {
    const payload = {
      port_type: selectedPort?.label,
      ports: selectedPort?.value,
      config_id: selectedConfig?.value,
      target_id: data?.target,
      credential_id: Number(data?.credential),
      autoStartScan: autoStartScan,
      scanner_id: data?.scanners,
      ...(scheduleScan && {
        isScheduled: true,
        schedule: {
          type: data?.scheduleType,
          startDate: data?.startDate,
          startTime: convertToUTC(data?.startTime?.slice(0, 5)),
        },
      }),
    };
    mutate(payload);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen} inert>
      <DialogTrigger asChild>
        <Button className="w-fit px-3">
          <FiCompass />
          <span className="font-medium">Create scan</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="">
        <DialogHeader className="text-left">
          <DialogTitle className="text-md text-semibold text-black">
            Create Scan
          </DialogTitle>
          <DialogDescription>
            Scan to check for known vulnerabilities or misconfigurations.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 mt-4"
          >
            <div className="flex flex-col">
              <label
                htmlFor="inputType"
                className="text-sm font-medium text-neutral1000 mb-3"
              >
                Scan Type
              </label>
              <RadioGroup
                // defaultValue="manual"
                value={isAssetGroup}
                className="flex items-center gap-12 mb-2"
                onValueChange={(value) => setIsAssetGroup(value)}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value={true} id="r1" />
                  <Label htmlFor="r1" className="m-0">
                    Asset Group
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value={false} id="r2" />
                  <Label htmlFor="r2" className="m-0">
                    Custom Target
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <FormField
              control={form.control}
              name="target"
              render={({ field }) => {
                const options = isAssetGroup
                  ? assetGroupList?.data?.map((item) => ({
                      value: String(item.id),
                      label: item.name,
                    }))
                  : targetOptions?.map((type) => ({
                      value: String(type.value),
                      label: type.label,
                    }));

                return (
                  <FormItem className="mb-4">
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="h-11">
                          <SelectValue
                            placeholder={
                              isAssetGroup
                                ? "Select an asset"
                                : "Select a target"
                            }
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {options?.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />

            {/* <FormField
              control={form.control}
              name="ports"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>Ports</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value); // Update the form field value
                      const selectedLabel = portOptions.find(
                        (option) => option.value === value
                      )?.label;
                      setPortType(selectedLabel); // Update the selected port label
                    }}
                    value={field?.value}
                  >
                    <FormControl>
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Select a port" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {portOptions?.map((type) =>
                        type.label === "All IANA assigned TCP and UDP" ? (
                          <SelectItem key={type.label} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ) : null
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            /> */}
            <FormField
              control={form.control}
              name="scanners"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>Scanners</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field?.value}
                    disabled={!targetType}
                  >
                    <FormControl>
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Select a scanner" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {filteredScannerOptions?.map((type) => (
                        <SelectItem key={type.label} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            {/* <FormField
              control={form.control}
              name="configs"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>Configs</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field?.value}
                    // disabled={disabled}
                  >
                    <FormControl>
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Select config" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {configOptions?.map((type) =>
                        type.label === "Full and fast" ? (
                          <SelectItem key={type.label} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ) : null
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            /> */}

            <FormField
              control={form.control}
              name="credential"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>Credential (Optional)</FormLabel>
                  <Select
                    onValueChange={(value) =>
                      field.onChange(value === "none" ? "" : value)
                    }
                    value={field.value || "none"}
                  >
                    <FormControl>
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Select credential" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">No option</SelectItem>
                      {credentials?.map((type) => (
                        <SelectItem key={type.id} value={String(type.id)}>
                          {type.name ?? "invalid"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="scanType"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>Scan Mode</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={(value) => {
                        field.onChange(value);
                        setScheduleScan(value === "scheduled");
                        setAutoStartScan(value === "automatic");
                      }}
                      value={field.value}
                      className="grid grid-cols-2"
                    >
                      <FormItem className="flex items-center gap-3 cursor-pointer">
                        <FormControl>
                          <RadioGroupItem value="automatic" className="mt-2" />
                        </FormControl>
                        <FormLabel className="font-medium cursor-pointer">
                          Automatic
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center gap-3 cursor-pointer">
                        <FormControl>
                          <RadioGroupItem value="scheduled" className="mt-2" />
                        </FormControl>
                        <FormLabel className="font-medium cursor-pointer">
                          Scheduled
                        </FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {scheduleScan && (
              <>
                <FormField
                  control={form.control}
                  name="scheduleType"
                  render={({ field }) => (
                    <FormItem className="mb-4">
                      <FormLabel>Schedule Type</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field?.value}
                        // disabled={disabled}
                      >
                        <FormControl>
                          <SelectTrigger className="h-11">
                            <SelectValue placeholder="Select a type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="one-off">Once</SelectItem>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="md:flex gap-4 justify-between items-center">
                  <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field }) => {
                      return (
                        <FormItem className="flex flex-col md:w-1/2 mb-4">
                          <FormLabel>Start Date</FormLabel>
                          <FormControl>
                            <Input {...field} type="date" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />

                  <FormField
                    control={form.control}
                    name="startTime"
                    render={({ field }) => (
                      <FormItem className="flex flex-col md:w-1/2 mb-4">
                        <FormLabel>Start time</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="time"
                            id="time-picker"
                            step="1"
                            className="bg-background [&::-webkit-calendar-picker-indicator]:mr-0 "
                          />
                        </FormControl>

                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </>
            )}

            <div className="flex justify-end space-x-4 pt-4">
              <Button
                type="submit"
                className="px-4 py-2 font-medium text-white rounded-full"
                loading={isPending}
              >
                Scan
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default ScanAssetModal;
